import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Plus, Globe, Save, Trash2, Power } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, handleErr } from "./AdminUI";

const Inp = (props) => (
  <input {...props} className={`h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60 ${props.className || ""}`} />
);

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [prices, setPrices] = useState({}); // { planId: { CODE: value } }
  const [loading, setLoading] = useState(true);
  const [newCur, setNewCur] = useState({ code: "", symbol: "", name: "", countries: "" });

  const load = async () => {
    try {
      const [pr, cr] = await Promise.all([endpoints.plans(), endpoints.adminCurrencies()]);
      const pl = pr.data || [];
      const cs = cr.data?.currencies || [];
      setPlans(pl);
      setCurrencies(cs);
      const map = {};
      for (const p of pl) {
        map[p.id] = {};
        for (const c of cs) {
          const v = (p.prices || {})[c.code];
          map[p.id][c.code] = v != null ? String(v) : "";
        }
      }
      setPrices(map);
    } catch (e) { handleErr(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setPrice = (planId, code, val) =>
    setPrices((m) => ({ ...m, [planId]: { ...(m[planId] || {}), [code]: val } }));

  const savePlan = async (p) => {
    const body = {};
    const row = prices[p.id] || {};
    for (const c of currencies) {
      const raw = row[c.code];
      if (raw !== "" && raw != null && !isNaN(Number(raw))) body[c.code] = Number(raw);
    }
    try {
      await endpoints.adminUpdatePlan(p.id, { prices: body, amount: body.RWF != null ? Number(body.RWF) : undefined });
      toast.success(`${p.label} prices saved`);
      load();
    } catch (e) { handleErr(e); }
  };

  const addCurrency = async () => {
    const code = newCur.code.trim().toUpperCase();
    if (!code) return toast.error("Enter a currency code (e.g. GBP)");
    try {
      await endpoints.adminCreateCurrency({
        code, symbol: newCur.symbol.trim() || code, name: newCur.name.trim() || code,
        countries: newCur.countries.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
        active: true,
      });
      toast.success(`${code} added`);
      setNewCur({ code: "", symbol: "", name: "", countries: "" });
      load();
    } catch (e) { handleErr(e); }
  };

  const updateCurrency = async (c, patch) => {
    try { await endpoints.adminUpdateCurrency(c.code, patch); load(); }
    catch (e) { handleErr(e); }
  };

  const saveCurrencyMeta = async (c) => {
    try {
      await endpoints.adminUpdateCurrency(c.code, {
        symbol: c.symbol, name: c.name,
        countries: (typeof c.countries === "string" ? c.countries.split(",") : c.countries || []).map((x) => String(x).trim().toUpperCase()).filter(Boolean),
      });
      toast.success(`${c.code} updated`);
      load();
    } catch (e) { handleErr(e); }
  };

  const delCurrency = async (c) => {
    if (c.isBase) return toast.error("The base currency (RWF) cannot be removed");
    if (!window.confirm(`Remove ${c.code}? Customers will no longer see this currency.`)) return;
    try { await endpoints.adminDeleteCurrency(c.code); toast.success(`${c.code} removed`); load(); }
    catch (e) { handleErr(e); }
  };

  const editMeta = (code, key, val) =>
    setCurrencies((cs) => cs.map((c) => c.code === code ? { ...c, [key]: val } : c));

  if (loading) return <Loading />;
  const activeCurrencies = currencies.filter((c) => c.active !== false);

  return (
    <div data-testid="admin-pricing-page">
      <PageHeader title="Pricing & Currencies" desc="Full control of subscription prices for every currency. Change prices anytime — no code or redeploy needed." />

      {/* Currencies manager */}
      <Card className="p-6 mb-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><Globe className="w-5 h-5 text-[#E11D2A]" /> Currencies</h3>
        <p className="text-xs text-slate-400 mb-4">Add currencies, set their symbol and the countries that use them, and activate/deactivate them. MTN MoMo always charges in <b>RWF</b>; other currencies are paid by Card or PayPal.</p>

        <div className="space-y-3">
          {currencies.map((c) => (
            <div key={c.code} data-testid={`currency-row-${c.code}`} className="grid grid-cols-1 sm:grid-cols-[5rem_5rem_1fr_1.4fr_auto] gap-3 items-center rounded-xl border border-[#273042] bg-[#0A0C10] p-3">
              <div className="font-bold text-white flex items-center gap-2">{c.code}{c.isBase && <span className="text-[9px] uppercase text-amber-400 border border-amber-400/40 rounded px-1">base</span>}</div>
              <Inp value={c.symbol} onChange={(e) => editMeta(c.code, "symbol", e.target.value)} data-testid={`currency-symbol-${c.code}`} placeholder="€" />
              <Inp value={c.name} onChange={(e) => editMeta(c.code, "name", e.target.value)} placeholder="Name" />
              <Inp value={Array.isArray(c.countries) ? c.countries.join(", ") : (c.countries || "")} onChange={(e) => editMeta(c.code, "countries", e.target.value)}
                data-testid={`currency-countries-${c.code}`} placeholder="Countries e.g. FR, DE, ES" />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => updateCurrency(c, { active: !(c.active !== false) })} data-testid={`currency-toggle-${c.code}`}
                  disabled={c.isBase}
                  className={`h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1 border ${c.active !== false ? "border-[#22C55E]/50 text-[#22C55E]" : "border-slate-600 text-slate-400"} ${c.isBase ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <Power className="w-3.5 h-3.5" /> {c.active !== false ? "On" : "Off"}
                </button>
                <Btn variant="ghost" onClick={() => saveCurrencyMeta(c)} data-testid={`currency-save-${c.code}`}><Save className="w-4 h-4" /></Btn>
                {!c.isBase && <button onClick={() => delCurrency(c)} data-testid={`currency-delete-${c.code}`} className="h-9 w-9 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 border-t border-[#273042] pt-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Add a currency</div>
          <div className="grid grid-cols-1 sm:grid-cols-[6rem_6rem_1fr_1.4fr_auto] gap-3">
            <Inp value={newCur.code} onChange={(e) => setNewCur({ ...newCur, code: e.target.value.toUpperCase() })} data-testid="new-currency-code" placeholder="GBP" maxLength={5} />
            <Inp value={newCur.symbol} onChange={(e) => setNewCur({ ...newCur, symbol: e.target.value })} data-testid="new-currency-symbol" placeholder="£" />
            <Inp value={newCur.name} onChange={(e) => setNewCur({ ...newCur, name: e.target.value })} placeholder="British Pound" />
            <Inp value={newCur.countries} onChange={(e) => setNewCur({ ...newCur, countries: e.target.value })} data-testid="new-currency-countries" placeholder="Countries e.g. GB" />
            <Btn onClick={addCurrency} data-testid="add-currency-btn"><Plus className="w-4 h-4" /> Add</Btn>
          </div>
        </div>
      </Card>

      {/* Price matrix */}
      <Card className="p-6">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><DollarSign className="w-5 h-5 text-[#E11D2A]" /> Plan prices</h3>
        <p className="text-xs text-slate-400 mb-4">Set the Basic and Premium price in each active currency independently. Prices are shown to customers exactly as entered and sent to the payment provider.</p>

        <div className="space-y-5">
          {plans.map((p) => (
            <div key={p.id} data-testid={`price-plan-${p.id}`} className="rounded-xl border border-[#273042] bg-[#0A0C10] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white font-bold capitalize">{p.tier} · {p.period}</div>
                <Btn variant="ghost" onClick={() => savePlan(p)} data-testid={`price-save-${p.id}`}><Save className="w-4 h-4" /> Save</Btn>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {activeCurrencies.map((c) => (
                  <div key={c.code}>
                    <label className="text-[11px] text-slate-400 font-semibold block mb-1">{c.symbol} {c.code}</label>
                    <Inp type="number" step="0.01" min="0" value={(prices[p.id] || {})[c.code] ?? ""}
                      onChange={(e) => setPrice(p.id, c.code, e.target.value)}
                      data-testid={`price-${p.id}-${c.code}`} placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
